import type { TFunction } from 'i18next';
import { bool, object, string } from 'yup';

import {
  makeEmail,
  makeOrgDescription,
  makePersona,
} from 'src/modules/onboarding/validations/validations';
import type { CheckEmailAuthenticationMethod } from 'src/shared/hooks/useAuthenticationMethod';
import {
  makeNewPassword as makeNewSignUpPassword,
  makePassword,
  makeTerms,
} from 'src/shared/validationSchemas';
import { make as makePhoneNumber } from 'src/shared/validationSchemas/phoneNumber';

export const makeEmailValidationSchema = (t: TFunction) =>
  object().shape({
    email: makeEmail(t),
  });

export const makeSingleSignOnValidationSchema = (
  t: TFunction,
  checkEmail: CheckEmailAuthenticationMethod | null,
) =>
  object().shape({
    email: makeEmail(t).test(
      'isSingleSignOnEnabled',
      t('onboarding.thisEmailIsNotSSOEnabled'),
      async email => {
        if (!makeEmail(t).isValidSync(email) || !checkEmail) return false;

        return checkEmail(email, { beacon: false, singleSignOn: true });
      },
    ),
  });

export const makeOrgDescriptionValidationSchema = () =>
  object().shape({
    orgDescription: makeOrgDescription(),
    customDescription: string(),
  });

export const makeOrgUsageValidationSchema = () =>
  object().shape({
    orgUsage: makePersona(),
    customDescription: string().optional(),
  });

export const makePasswordValidationSchema = (t: TFunction) =>
  object().shape({
    email: makeEmail(t),
    password: makePassword(t),
  });

export const makeHomeownerSignupValidationSchema = (t: TFunction) =>
  object().shape({
    name: string().required(t('onboarding.nameIsRequired')),
    email: makeEmail(t),
    password: makePassword(t),
  });

export const makeSignUpPasswordValidationSchema = (t: TFunction) =>
  object().shape({
    password: makeNewSignUpPassword(t),
    email: makeEmail(t),
  });

export const makePersonalInfoValidationSchema = (t: TFunction) =>
  object().shape({
    companyName: string().required(t('onboarding.companyIsRequired')),
    lastName: string().required(t('onboarding.lastNameIsRequired')),
    firstName: string().required(t('onboarding.firstNameIsRequired')),
    phoneNumber: makePhoneNumber(
      t,
      'onboarding.mobilePhoneIsRequired',
      'onboarding.pleaseProvideAValidPhoneNumber',
    ),
    hasAgreedToTerms: makeTerms(t, 'onboarding.termsAgreementIsRequired'),
    contactPolicy: object().optional().shape({
      hasConsent: bool().optional(),
    }),
  });
